/**
 * assetsView.ts — pure view-model helpers for Operations / Assets (M4).
 *
 * The wire stores category and status as lowercase slugs (0102's CHECKs);
 * this module owns the labels — the modal's category and status options
 * (klaxiora-crm-mockup-full.html :3143-3149, :3159-3163) plus 'Retired',
 * the deliberate end-of-life extension beyond the mockup's three (decision
 * M4: retire-via-status instead of delete) — the count line (:5171), and
 * the KPI computations (:5160-5183). The needs-attention rule is the
 * mockup's (:5163-5166) with two honesty corrections: today is the REAL
 * clock passed in (the mockup hardcodes '2026-07-27'), and a retired asset
 * NEVER flags — a decommissioned laptop with a lapsed service date is not
 * actionable.
 */
import type { OpsAsset } from '../../types'

export const ASSET_CATEGORIES = [
  { slug: 'laptop',           label: 'Laptop' },
  { slug: 'vehicle',          label: 'Vehicle' },
  { slug: 'tool',             label: 'Tool' },
  { slug: 'software_licence', label: 'Software Licence' },
  { slug: 'other',            label: 'Other' },
] as const

export const ASSET_STATUSES = [
  { slug: 'in_use',      label: 'In Use' },
  { slug: 'available',   label: 'Available' },
  { slug: 'maintenance', label: 'Maintenance' },
  { slug: 'retired',     label: 'Retired' },
] as const

export function assetCategoryLabel(slug: string): string {
  return ASSET_CATEGORIES.find(c => c.slug === slug)?.label ?? slug
}

export function assetStatusLabel(slug: string): string {
  return ASSET_STATUSES.find(s => s.slug === slug)?.label ?? slug
}

/** The header count line — the mockup's renderAssetsList wording (:5171). */
export function assetCountLine(n: number): string {
  return `${n} asset${n !== 1 ? 's' : ''} on file.`
}

/** Local calendar date of `now` as "YYYY-MM-DD", for comparison against the
 *  wire's plain-date nextServiceDate — string compare, the mockup's rule.
 *  Exported since M6: overviewView's tasks-today uses the same comparison
 *  against ops_tasks.scheduledDate. */
export function localISODate(now: Date): string {
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${m}-${d}`
}

/** Needs attention = (maintenance OR service date ≤ today) AND NOT retired
 *  (decision M4 — a retired asset never flags, whatever its dates). */
export function assetNeedsAttention(a: OpsAsset, now: Date): boolean {
  if (a.status === 'retired') return false
  if (a.status === 'maintenance') return true
  return !!a.nextServiceDate && a.nextServiceDate <= localISODate(now)
}

export interface AssetKpis {
  total: number
  assigned: number
  available: number
  attention: number
}

/** The four KPI cards (:1973-1978): total on file, assigned (in someone's
 *  hands), available (status), needs attention per assetNeedsAttention. */
export function assetKpis(assets: OpsAsset[], now: Date): AssetKpis {
  return {
    total: assets.length,
    assigned: assets.filter(a => !!a.assignedTo).length,
    available: assets.filter(a => a.status === 'available').length,
    attention: assets.filter(a => assetNeedsAttention(a, now)).length,
  }
}
